import * as React from 'react';

import {formEncodedApi} from 'nutshell-core/api';

import {colors} from 'shells/colors/colors';
import {Illustration} from 'shells/illustrations/illustration';
import {ImageWrapper} from 'shells/onboarding-modal/image-wrapper';
import {OnboardingModal} from 'shells/onboarding-modal/onboarding-modal';
import {SingleInputModal} from 'shells/single-input-modal/single-input-modal';

import {analyticsClient} from '../../../../analytics-client';
import {appHistory} from '../../../../history';

import {NewEditionModal} from '../../editions/new-edition-modal';
import {AudienceMutations} from '../../graphql-wrappers/audience/audience-mutations';
import {BroadcastMutations} from '../../graphql-wrappers/broadcast/broadcast-mutations';

import {
    BodyIntroduction,
    EmailOutreach,
    AudiencesReceiveOutreach,
    HowItFitsTogether,
    GetStarted,
} from './pages';
import {
    GET_STARTED_MESSAGE_KEY,
    GET_STARTED_AUDIENCE_KEY,
    GET_STARTED_EXPLORE_KEY,
} from './pages/get-started';

type OwnProps = {
    // If onClose is passed, we will always show the curtain modal. This is used
    // for the checklist, allowing the user to view this flow multiple times.
    onClose?: () => void;
};

type Props = {
    onCreateAudience: (arg1: string) => Promise<any>;
} & OwnProps;

function CurtainModalComponent(props: Props) {
    const [showOnboardingCurtain, setShowOnboardingCurtain] = React.useState<boolean>(
        NutClientConfig.shouldShowEmailMarketingLiftingOfTheCurtain
    );
    const [showOnboardingCurtainCTA] = React.useState<boolean>(
        NutClientConfig.shouldShowEmailMarketingLiftingOfTheCurtainCTA
    );
    const [selectedCard, setSelectedCard] = React.useState<string>(GET_STARTED_EXPLORE_KEY);
    const [isAddingNewBroadcast, setIsAddingNewBroadcast] = React.useState<boolean>(false);
    const [isAddingNewAudience, setIsAddingNewAudience] = React.useState<boolean>(false);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);

    const pages = [
        {
            id: 'BodyIntroduction',
            body: <BodyIntroduction />,
            art: {
                background: colors.plumLt,
                image: () => (
                    <ImageWrapper image={<Illustration src='email-airplane' />} width={270} />
                ),
            },
        },
        {
            id: 'EmailOutreach',
            body: <EmailOutreach />,
            art: {
                background: colors.plumLt,
                image: () => (
                    <ImageWrapper image={<Illustration src='email-message' />} width={170} />
                ),
            },
        },
        {
            id: 'AudiencesReceiveOutreach',
            body: <AudiencesReceiveOutreach />,
            art: {
                background: colors.plumLt,
                image: () => (
                    <ImageWrapper image={<Illustration src='message-audience' />} width={230} />
                ),
            },
        },
        {
            id: 'HowItFitsTogether',
            body: <HowItFitsTogether />,
            art: {
                background: colors.plumLt,
                image: () => (
                    <ImageWrapper
                        image={<Illustration src='message-audience-graph' />}
                        width={220}
                    />
                ),
            },
        },
    ];

    if (showOnboardingCurtainCTA) {
        pages.push({
            id: 'GetStarted',
            body: <GetStarted selectedCard={selectedCard} onSelectCard={setSelectedCard} />,
            art: {
                background: colors.plumLt,
                image: () => <ImageWrapper image={<Illustration src='trophy' />} width={200} />,
            },
        });
    }

    if (!showOnboardingCurtain && !props.onClose) {
        return null;
    }

    const getAnalyticsEventForPageView = (pageNumber: number) => {
        switch (pageNumber) {
            case 0:
                return analyticsClient.EVENTS.TRACK_EM_LOTC_0_INTRODUCTION;
            case 1:
                return analyticsClient.EVENTS.TRACK_EM_LOTC_1_EMAIL_OUTREACH;
            case 2:
                return analyticsClient.EVENTS.TRACK_EM_LOTC_2_AUDIENCES_RECEIVE_OUTREACH;
            case 3:
                return analyticsClient.EVENTS.TRACK_EM_LOTC_3_HOW_IT_FITS;
            case 4:
                return analyticsClient.EVENTS.TRACK_EM_LOTC_4_GET_STARTED;
            default:
                return analyticsClient.EVENTS.TRACK_EM_LOTC_UNKNOWN_VIEWED;
        }
    };

    const closeModal = () => {
        setShowOnboardingCurtain(false);

        formEncodedApi
            .post('/my-account/save-user-setting.json', {
                setting: 'has_viewed_email_marketing_lifting_of_the_curtain',
                value: 1,
            })
            .then(() => {
                NutClientConfig.shouldShowEmailMarketingLiftingOfTheCurtain = false;
            });
        formEncodedApi
            .post('/onboarding/save-setting.json', {
                setting: 'has_viewed_email_marketing_lifting_of_the_curtain_cta',
                value: 1,
            })
            .then(() => {
                NutClientConfig.shouldShowEmailMarketingLiftingOfTheCurtainCTA = false;
            });

        if (props.onClose) {
            props.onClose();
        }
    };

    const onClose = () => {
        if (selectedCard === GET_STARTED_MESSAGE_KEY) {
            // Create a new broadcast
            setIsAddingNewBroadcast(true);
        } else if (selectedCard === GET_STARTED_AUDIENCE_KEY) {
            // Create an audience
            setIsAddingNewAudience(true);
        } else {
            // Just close
            closeModal();
        }
    };

    return (
        <BroadcastMutations>
            {({onCreate, onDuplicate}) => (
                <React.Fragment>
                    <OnboardingModal
                        trapFocus={false}
                        lastButtonText='Get started'
                        pages={pages}
                        onClose={onClose}
                        onFinish={onClose}
                        analyticsClient={analyticsClient}
                        getAnalyticsEventForPageView={getAnalyticsEventForPageView}
                        canClickOutsideToClose={false}
                    />
                    {isAddingNewAudience ? (
                        <SingleInputModal
                            requireValue={true}
                            buttonText='Create'
                            initialValue='My first audience'
                            title='Name your audience'
                            subtitle='Add a descriptive name so you can find it later'
                            placeholder='My first audience'
                            isSaving={isSaving}
                            onClose={() => {
                                setIsAddingNewAudience(false);
                                setIsAddingNewBroadcast(false);
                            }}
                            onSave={(name) => {
                                setIsSaving(true);
                                // Create an audience and send the user to that page
                                props.onCreateAudience(name).then((newAudience) => {
                                    closeModal();

                                    if (!newAudience) return;

                                    appHistory.push(newAudience.htmlUrlPath);
                                });
                            }}
                        />
                    ) : undefined}
                    <NewEditionModal
                        // TODO: This is a hack to get flow to pass, but we don't technically
                        // need a campaignId here, so I'm hacking it.
                        campaignId='broadcast'
                        type='broadcasts'
                        // @ts-expect-error
                        defaultView='suggested'
                        isOpen={isAddingNewBroadcast}
                        onClose={() => {
                            setIsAddingNewBroadcast(false);
                        }}
                        onCreateEdition={(name) => {
                            return onCreate(name).then((newBroadcast) => {
                                if (newBroadcast) appHistory.push(newBroadcast.edition.htmlUrlPath);
                                closeModal();
                            });
                        }}
                        onDuplicateEdition={(
                            editionId: string,
                            campaignId: string,
                            name?: string | null
                        ) => {
                            return onDuplicate(editionId, name).then((newBroadcast) => {
                                if (newBroadcast) appHistory.push(newBroadcast.edition.htmlUrlPath);
                                closeModal();
                            });
                        }}
                    />
                </React.Fragment>
            )}
        </BroadcastMutations>
    );
}

export function CurtainModal(props: OwnProps) {
    return (
        <AudienceMutations>
            {({onCreate}) => <CurtainModalComponent onCreateAudience={onCreate} {...props} />}
        </AudienceMutations>
    );
}
